import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Tabs, 
  Tab, 
  Badge,
  Alert,
  Button
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PhysiologicalRiskEvent } from '../../types/wearable';
import PhysiologicalAlertCard from './PhysiologicalAlertCard';

interface PhysiologicalAlertListProps {
  events?: PhysiologicalRiskEvent[];
  loading?: boolean;
  error?: string;
  onResolve?: (eventId: string) => void;
  onRefresh?: () => void;
}

const PhysiologicalAlertList: React.FC<PhysiologicalAlertListProps> = ({
  events = [],
  loading = false,
  error,
  onResolve,
  onRefresh
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [filteredEvents, setFilteredEvents] = useState<PhysiologicalRiskEvent[]>([]);

  // 활성/해결된 이벤트 필터링
  useEffect(() => {
    if (tabValue === 0) {
      // 활성 이벤트만 표시
      setFilteredEvents(events.filter(event => event.status === 'active'));
    } else {
      // 해결된 이벤트만 표시
      setFilteredEvents(events.filter(event => event.status === 'resolved'));
    }
  }, [events, tabValue]);

  // 위험 수준별 정렬
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const riskLevelOrder = { 'high': 0, 'medium': 1, 'low': 2 };
    const riskA = riskLevelOrder[a.risk_level.toLowerCase()] || 99;
    const riskB = riskLevelOrder[b.risk_level.toLowerCase()] || 99;
    
    if (riskA !== riskB) {
      return riskA - riskB; // 위험도 순 정렬
    }
    
    // 동일 위험도는 최신순 정렬
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // 활성 탭 변경 핸들러
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // 활성 및 해결된 이벤트 수
  const activeCount = events.filter(event => event.status === 'active').length;
  const resolvedCount = events.filter(event => event.status === 'resolved').length;

  return (
    <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">생체 신호 위험 알림</Typography>
        
        <Button 
          startIcon={<RefreshIcon />} 
          size="small" 
          onClick={onRefresh}
          disabled={loading}
        >
          새로고침
        </Button>
      </Box>
      
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        sx={{ mb: 2 }}
        variant="fullWidth"
      >
        <Tab 
          label={
            <Badge badgeContent={activeCount} color="error" max={99}>
              <Box sx={{ mx: 1 }}>활성 이벤트</Box>
            </Badge>
          } 
        />
        <Tab 
          label={
            <Badge badgeContent={resolvedCount} color="success" max={99}>
              <Box sx={{ mx: 1 }}>해결된 이벤트</Box>
            </Badge>
          } 
        />
      </Tabs>
      
      {/* 로딩 상태 */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* 에러 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* 이벤트 없음 메시지 */}
      {!loading && !error && sortedEvents.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <Typography color="text.secondary">
            {tabValue === 0 ? '활성 이벤트 없음' : '해결된 이벤트 없음'}
          </Typography>
        </Box>
      )}
      
      {/* 이벤트 목록 */}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {sortedEvents.map(event => (
          <PhysiologicalAlertCard 
            key={event.id} 
            event={event} 
            onResolve={onResolve}
          />
        ))}
      </Box>
    </Paper>
  );
};

export default PhysiologicalAlertList; 